import React, { useEffect, useState } from 'react';
import { Alert, Spinner, Table, ButtonGroup, Button, Badge } from 'react-bootstrap';
import moment from 'moment';
import { SurveysAPI } from '../../API';
import CardHeader from '../Card/CardHeader';
import GridContainer from '../Grid/GridContainer';

// Universal returns B2 — redirect health (read-only). Partner entries sent
// to the client survey vs returns through our end links, per client,
// survey-platform host and survey, over 2h / 24h / 7d. Red: >= 20 entries
// under 40% returned (24h or 7d), or >= 10 entries in the last 2h with no
// return in the last 2h. Panel respondents are not counted.

type Win = { entries: number; returned: number; returnPct: number | null };
type Group = {
    client?: string; host?: string; surveyId?: string; uniqueid?: string; surveyName?: string;
    windows: { '2h': Win; '24h': Win; '7d': Win };
    returns2h: number; lastReturnAt: string | null; red: boolean; redReasons: string[];
};
type Report = { generatedAt: string; byClient: Group[]; byHost: Group[]; bySurvey: Group[]; red: { level: string; label: string; reasons: string[] }[] };
type View = 'byClient' | 'byHost' | 'bySurvey';

const WINDOWS: Array<'2h' | '24h' | '7d'> = ['2h', '24h', '7d'];

const pctText = (w: Win) => (w.returnPct === null ? '—' : `${w.returnPct}%`);

export const RedirectHealth: React.FC = () => {
    const [report, setReport] = useState<Report | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState<View>('byClient');
    const [onlyRed, setOnlyRed] = useState(false);

    const load = () => {
        setLoading(true);
        SurveysAPI.redirectHealth()
            .then((r: Report) => { setReport(r); setError(null); })
            .catch((e: any) => setError(e?.message || 'Could not load redirect health'))
            .finally(() => setLoading(false));
    };
    useEffect(load, []);

    const groups = report ? report[view].filter((g) => !onlyRed || g.red) : [];

    return (
        <>
            <GridContainer>
                <div className="w-100 mb-3">
                    <CardHeader color="primary">
                        <div className="d-flex align-items-center justify-content-between">
                            <h4 style={{ margin: 0 }}>Redirect health</h4>
                            <small style={{ opacity: 0.8 }}>
                                Partner entries vs returns through our end links{report ? ` · ${moment(report.generatedAt).format('DD MMM HH:mm')}` : ''}
                            </small>
                        </div>
                    </CardHeader>
                </div>
            </GridContainer>
            <div className="d-flex flex-wrap align-items-center mb-2" style={{ gap: 8 }}>
                <ButtonGroup size="sm">
                    <Button variant={view === 'byClient' ? 'primary' : 'outline-primary'} onClick={() => setView('byClient')}>By client</Button>
                    <Button variant={view === 'byHost' ? 'primary' : 'outline-primary'} onClick={() => setView('byHost')}>By platform host</Button>
                    <Button variant={view === 'bySurvey' ? 'primary' : 'outline-primary'} onClick={() => setView('bySurvey')}>By survey</Button>
                </ButtonGroup>
                <label className="mb-0 ml-2" style={{ fontSize: '0.9em' }}>
                    <input type="checkbox" className="mr-1" checked={onlyRed} onChange={(e) => setOnlyRed(e.target.checked)} />
                    Red only
                </label>
                <Button size="sm" variant="outline-secondary" onClick={load} disabled={loading}>Refresh</Button>
                <small className="text-muted">
                    Red: 20+ entries with under 40% returned (24h or 7d), or 10+ entries in the last 2h and no return in the last 2h. Panel respondents are not counted.
                </small>
            </div>
            <Alert variant="danger" show={!!error}>{error}</Alert>
            {loading && !report ? (
                <div className="d-flex justify-content-center p-4"><Spinner animation="border" /></div>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <Table size="sm" bordered hover>
                        <thead>
                            <tr>
                                {view !== 'byHost' && <th rowSpan={2}>Client</th>}
                                {view !== 'byClient' && <th rowSpan={2}>Platform host</th>}
                                {view === 'bySurvey' && <th rowSpan={2}>Survey</th>}
                                {WINDOWS.map((w) => <th key={w} colSpan={3} className="text-center">{w}</th>)}
                                <th rowSpan={2}>Last return</th>
                                <th rowSpan={2}>Flag</th>
                            </tr>
                            <tr>
                                {WINDOWS.map((w) => (
                                    <React.Fragment key={w}>
                                        <th>Entries</th><th>Returned</th><th>Return %</th>
                                    </React.Fragment>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {groups.length === 0 && (
                                <tr><td colSpan={20} className="text-center text-muted">No partner entries in the last 7 days{onlyRed ? ' flagged red' : ''}.</td></tr>
                            )}
                            {groups.map((g, i) => (
                                <tr key={`${g.client}|${g.host}|${g.surveyId}|${i}`} style={g.red ? { background: '#fdecea' } : undefined}>
                                    {view !== 'byHost' && <td>{g.client}</td>}
                                    {view !== 'byClient' && <td>{g.host}</td>}
                                    {view === 'bySurvey' && <td>{g.surveyName} <span className="text-muted">({g.uniqueid})</span></td>}
                                    {WINDOWS.map((w) => (
                                        <React.Fragment key={w}>
                                            <td className="text-right">{g.windows[w].entries}</td>
                                            <td className="text-right">{g.windows[w].returned}</td>
                                            <td className="text-right">{pctText(g.windows[w])}</td>
                                        </React.Fragment>
                                    ))}
                                    <td>{g.lastReturnAt ? moment(g.lastReturnAt).format('DD MMM HH:mm') : '—'}</td>
                                    <td>
                                        {g.red ? <Badge variant="danger" title={g.redReasons.join('\n')}>Red</Badge> : null}
                                        {g.red ? <div style={{ fontSize: '0.8em' }}>{g.redReasons.join('; ')}</div> : null}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            )}
        </>
    );
};

// The dashboard banner: the red items, linking to the page. Renders nothing
// while loading, on error, or when nothing is red.
export const RedirectHealthBanner: React.FC<{ href: string; onOpen: (href: string) => void }> = ({ href, onOpen }) => {
    const [red, setRed] = useState<Report['red']>([]);
    useEffect(() => {
        let live = true;
        SurveysAPI.redirectHealth()
            .then((r: Report) => { if (live) setRed(r.red || []); })
            .catch(() => { if (live) setRed([]); });
        return () => { live = false; };
    }, []);
    if (red.length === 0) return null;
    const shown = red.slice(0, 6);
    return (
        <Alert variant="danger" className="mb-3">
            <strong>Respondent returns look broken</strong> for {red.length} item{red.length === 1 ? '' : 's'}:
            <ul className="mb-1 mt-1">
                {shown.map((r, i) => (
                    <li key={i}><span className="text-capitalize">{r.level}</span> {r.label}: {r.reasons.join('; ')}</li>
                ))}
            </ul>
            {red.length > shown.length ? <div>…and {red.length - shown.length} more.</div> : null}
            <Button size="sm" variant="outline-danger" onClick={() => onOpen(href)}>Open redirect health</Button>
        </Alert>
    );
};
